import Api from 'api';
import { UserAccountProps } from 'utils/types';

interface QualificationSaveRequest {
  hygienistId: number;
  skillIds?: number[];
  qualificationLevel?: 1 | 2;
  languageIds: number[];
  isLeftHanded?: boolean;
  cdhoNumber?: string;
  isCurrent?: () => boolean;
}

const sameIds = (actual: number[], expected: number[]) => {
  const actualIds = new Set(actual);
  const expectedIds = new Set(expected);
  return actualIds.size === expectedIds.size && Array.from(expectedIds).every(id => actualIds.has(id));
};

/** These endpoints can save partially. A retry reconciles the current language
 * rows, and success requires read-back of every requested field. */
export async function saveQualification({
  hygienistId, skillIds, qualificationLevel, languageIds, isLeftHanded, cdhoNumber, isCurrent = () => true,
}: QualificationSaveRequest): Promise<UserAccountProps> {
  const ensureCurrent = () => {
    if (!isCurrent()) throw new Error('Qualification form is no longer active');
  };
  ensureCurrent();
  const [currentUser, currentLanguages] = await Promise.all([Api.getUser(), Api.getMyLanguages()]);
  ensureCurrent();
  const current = currentUser?.hygienist;
  if (!current || current.id !== hygienistId) throw new Error('Qualification account changed');
  // Repair only a missing Ontario credential. A retry may already have saved
  // this number; never replace a different credential added concurrently.
  const requestedCdho = cdhoNumber === undefined ? undefined : cdhoNumber.trim();
  if (requestedCdho !== undefined) {
    if (!requestedCdho || Number(current.type) !== 0
      || !['ON', 'ONTARIO'].includes(String(current.province || '').trim().toUpperCase())) {
      throw new Error('Ontario CDHO repair is unavailable');
    }
    const existing = String(current.CDHONumber ?? '').trim();
    if (existing && existing !== requestedCdho) throw new Error('Your saved licence changed. Reopen your profile before trying again.');
    if (!existing) {
      await Api.checkUniqueHygienistCDHONumber({ CDHONumber: requestedCdho, type: 0, province: 'ON' });
      ensureCurrent();
      await Api.updateHygienistAccount({ CDHONumber: requestedCdho, cdhoRepairOnly: true });
      ensureCurrent();
    }
  }
  // A prior attempt may already have saved these fields. Avoid repeating the
  // legacy destructive skill replacement when only another step needs retrying.
  const matchesQualification = (talent: typeof current) =>
    (qualificationLevel === undefined || talent.level === qualificationLevel)
    && talent.isOrtho === 0 && talent.isRestorative === 0;
  if (skillIds !== undefined && (!sameIds((current.skills || []).map(skill => skill.id), skillIds)
    || !matchesQualification(current))) {
    await Api.replaceHygienistSkills(skillIds);
    ensureCurrent();
  }
  if (isLeftHanded !== undefined && Boolean(current.isLeftHanded) !== isLeftHanded) {
    await Api.updateHygienistAccount({ isLeftHanded: isLeftHanded ? 1 : 0 });
    ensureCurrent();
  }

  const currentIds = currentLanguages.map(language => language.id);
  const toAdd = languageIds.filter(id => !currentIds.includes(id));
  const toRemove = currentIds.filter(id => !languageIds.includes(id));
  if (toAdd.length) {
    await Api.addLanguages(toAdd);
    ensureCurrent();
  }
  if (toRemove.length) {
    await Api.removeLanguages(toRemove);
    ensureCurrent();
  }

  const [user, savedLanguages] = await Promise.all([Api.getUser(), Api.getMyLanguages()]);
  ensureCurrent();
  const saved = user?.hygienist;
  if (!saved || saved.id !== hygienistId
    || (skillIds !== undefined && (!sameIds((saved.skills || []).map(skill => skill.id), skillIds)
      || !matchesQualification(saved)))
    || !sameIds(savedLanguages.map(language => language.id), languageIds)
    || (requestedCdho !== undefined && String(saved.CDHONumber ?? '').trim() !== requestedCdho)
    || (isLeftHanded !== undefined && Boolean(saved.isLeftHanded) !== isLeftHanded)) {
    throw new Error('Qualification save could not be confirmed');
  }
  return user;
}
